/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Cart, CartLine, ChargeLine, CommerceProperty } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { readLineOrderState, readStoreSetPrice } from './orderstate';

/** The store's own figures, stamped on the order by the pull (ISV 11003). */
const STORE_SUBTOTAL_AMOUNT = 'ITS_StoreSubtotalAmount';
const STORE_TAX_AMOUNT = 'ITS_StoreTaxAmount';
const STORE_TOTAL_AMOUNT = 'ITS_StoreTotalAmount';

/** The discount the till gave on the whole order, kept on the order when it is opened for changes (ISV 11026). */
const STORE_TOTAL_DISCOUNT_PERCENTAGE = 'ITS_StoreTotalDiscountPercentage';
const STORE_TOTAL_DISCOUNT_AMOUNT = 'ITS_StoreTotalDiscountAmount';

/** The discount as the till applied it, and each store line's share of it, kept on resume (ISV 11026). */
const STORE_TOTAL_DISCOUNT_EFFECTIVE_AMOUNT = 'ITS_StoreTotalDiscountEffectiveAmount';
const STORE_TOTAL_DISCOUNT_SHARE = 'ITS_StoreTotalDiscountShare';

export interface IStoreTotals {
    subtotal: number;
    tax: number;
    total: number;
}

const readDecimal = (properties: CommerceProperty[] | undefined, key: string): number | undefined => {
    const value = properties?.find(property => property.Key === key)?.Value?.DecimalValue;

    return typeof value === 'number' ? value : undefined;
};

/** The store's subtotal, tax and total for the order, only when the pull carried all three. */
export const readStoreTotals = (cart: Cart | undefined): IStoreTotals | undefined => {
    const subtotal = readDecimal(cart?.ExtensionProperties, STORE_SUBTOTAL_AMOUNT);
    const tax = readDecimal(cart?.ExtensionProperties, STORE_TAX_AMOUNT);
    const total = readDecimal(cart?.ExtensionProperties, STORE_TOTAL_AMOUNT);

    if (subtotal === undefined || tax === undefined || total === undefined) {
        return undefined;
    }

    return { subtotal, tax, total };
};

const roundToCents = (amount: number): number => Math.round(amount * 100) / 100;

/** A line's amount at the price the store set on it, or undefined for a line this channel priced or one voided. */
export const readLineAmountAtStorePrice = (line: CartLine | undefined): number | undefined => {
    const price = readStoreSetPrice(line);

    return price === undefined || line?.IsVoided ? undefined : roundToCents(price * (line?.Quantity ?? 0));
};

/**
 * The store's discount on the whole order: the shares the till gave the store lines still on it, plus the percentage
 * on lines added here. A package that stamps no shares has it worked out on the subtotal at the store's prices.
 */
export const readStoreOrderDiscount = (cart: Cart | undefined, subtotal: number): number => {
    const percentage = readDecimal(cart?.ExtensionProperties, STORE_TOTAL_DISCOUNT_PERCENTAGE) ?? 0;
    const amount = readDecimal(cart?.ExtensionProperties, STORE_TOTAL_DISCOUNT_AMOUNT) ?? 0;
    const lines = (cart?.CartLines ?? []).filter(line => !line.IsVoided);
    const shares = lines.map(line => readDecimal(line.ExtensionProperties, STORE_TOTAL_DISCOUNT_SHARE));

    if (shares.every(share => share === undefined)) {
        return percentage > 0 ? roundToCents((subtotal * percentage) / 100) : Math.max(amount, 0);
    }

    const sharesOnStoreLines = shares.reduce<number>((sum, share) => sum + (share ?? 0), 0);
    const isApplied = readDecimal(cart?.ExtensionProperties, STORE_TOTAL_DISCOUNT_EFFECTIVE_AMOUNT) !== 0;
    const addedHere = lines
        .filter(line => readLineOrderState(line).committed !== 'yes')
        .reduce((sum, line) => sum + (line.ExtendedPrice ?? 0), 0);

    return roundToCents(sharesOnStoreLines + (isApplied && percentage > 0 ? (addedHere * percentage) / 100 : 0));
};

const shiftBy = (amount: number | undefined, difference: number): number | undefined =>
    amount === undefined ? undefined : roundToCents(amount + difference);

/** The charge method that works a charge out as a percentage of its line. */
const PERCENTAGE_CHARGE_METHOD = 2;

const percentageOf = (amount: number, percentage: number | undefined): number => roundToCents((amount * (percentage ?? 0)) / 100);

const taxAt = (amount: number, percentage: number | undefined, current: number | undefined): number | undefined =>
    percentage === undefined ? current : percentageOf(amount, percentage);

/** The rate the tax was worked out at: the one stated, or where none is stated, the tax over the amount it was worked out on. */
const taxRateOf = (statedRate: number | undefined, tax: number | undefined, taxedAmount: number | undefined): number | undefined => {
    if (statedRate) {
        return statedRate;
    }

    return tax && taxedAmount ? (tax * 100) / taxedAmount : undefined;
};

/** A line the till priced or discounted, with its percentage charges and its tax worked out on what the till charges for it. */
const withStoreLineCharges = (line: CartLine): CartLine => {
    const share = readDecimal(line.ExtensionProperties, STORE_TOTAL_DISCOUNT_SHARE);
    const atStorePrice = readLineAmountAtStorePrice(line);
    if (
        share === undefined ||
        line.IsVoided ||
        readLineOrderState(line).committed !== 'yes' ||
        (atStorePrice === undefined && share === 0)
    ) {
        return line;
    }

    const net = (atStorePrice ?? line.NetAmountWithoutTax ?? line.ExtendedPrice ?? 0) - share;
    const chargeLines = (line.ChargeLines ?? []).map(charge => {
        if (charge.ChargeMethodValue !== PERCENTAGE_CHARGE_METHOD || charge.IsHeaderChargeProrated) {
            return charge;
        }

        const amount = percentageOf(net, charge.Value);
        const chargeTaxRate = taxRateOf(charge.TaxRatePercent, charge.TaxAmount, charge.CalculatedAmount);

        return { ...charge, CalculatedAmount: amount, TaxAmount: taxAt(amount, chargeTaxRate, charge.TaxAmount) };
    });
    const lineTaxRate = taxRateOf(line.TaxRatePercent, line.TaxAmount, line.NetAmountWithoutTax ?? line.ExtendedPrice);

    return { ...line, ChargeLines: chargeLines, TaxAmount: taxAt(net, lineTaxRate, line.TaxAmount) };
};

const sumOf = (amounts: (number | undefined)[]): number => amounts.reduce<number>((sum, amount) => sum + (amount ?? 0), 0);

const chargesOf = (lines: CartLine[]): ChargeLine[] => lines.reduce<ChargeLine[]>((all, line) => all.concat(line.ChargeLines ?? []), []);

/**
 * The cart as the till charges it: each line the store priced at the store's price, the store's discount on the whole
 * order as a discount, and the charges and tax on the lines the till holds worked out on those figures.
 */
export const withStorePrices = (cart: Cart): Cart => {
    const lineDifference = roundToCents(
        (cart.CartLines ?? []).reduce((sum, line) => {
            const atStorePrice = readLineAmountAtStorePrice(line);

            return atStorePrice === undefined ? sum : sum + atStorePrice - (line.ExtendedPrice ?? 0);
        }, 0)
    );
    const orderDiscount = readStoreOrderDiscount(cart, (cart.SubtotalAmount ?? 0) + lineDifference);

    const lines = cart.CartLines ?? [];
    const linesAtStoreFigures = cart.IsTaxIncludedInPrice ? lines : lines.map(withStoreLineCharges);
    const chargeDifference = roundToCents(
        sumOf(chargesOf(linesAtStoreFigures).map(charge => charge.CalculatedAmount)) -
            sumOf(chargesOf(lines).map(charge => charge.CalculatedAmount))
    );
    const chargeTaxDifference = roundToCents(
        sumOf(chargesOf(linesAtStoreFigures).map(charge => charge.TaxAmount)) - sumOf(chargesOf(lines).map(charge => charge.TaxAmount))
    );
    const taxDifference = roundToCents(
        sumOf(linesAtStoreFigures.map(line => line.TaxAmount)) - sumOf(lines.map(line => line.TaxAmount)) + chargeTaxDifference
    );

    if (lineDifference === 0 && orderDiscount === 0 && chargeDifference === 0 && taxDifference === 0) {
        return cart;
    }

    return {
        ...cart,
        CartLines: linesAtStoreFigures,
        SubtotalAmount: shiftBy(cart.SubtotalAmount, lineDifference),
        SubtotalAmountWithoutTax: shiftBy(cart.SubtotalAmountWithoutTax, lineDifference),
        DiscountAmount: roundToCents((cart.DiscountAmount ?? 0) + orderDiscount),
        ChargeAmount: shiftBy(cart.ChargeAmount, chargeDifference),
        OtherChargeAmount: shiftBy(cart.OtherChargeAmount, chargeDifference),
        TaxAmount: shiftBy(cart.TaxAmount, taxDifference),
        TaxOnNonShippingCharges: shiftBy(cart.TaxOnNonShippingCharges, chargeTaxDifference),
        TotalAmount: shiftBy(cart.TotalAmount, lineDifference - orderDiscount + chargeDifference + taxDifference)
    };
};
